import { useProduct } from '@mediashop/app/hooks/useProduct';
import { useAppDispatch } from '@mediashop/app/store/hooks/hooks';
import { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import MobileImageGallery from '../MobileImageGallery';
import ProductDetailsContent from './Content';
import Icon from '@mediashop/base/pattern/atom/Icon';
import BackgroundColorWrapper from '@mediashop/base/pattern/atom/BackgroundColorWrapper';
import ContentWrapper from '@mediashop/base/pattern/atom/ContentWrapper';
import { DetailedVariant } from '@mediashop/app/api/types/ClientProductDetailed';
import { useLocation } from 'react-router';
import Modal from '@mediashop/base/pattern/molecule/Modal';
import Badge from '@mediashop/catalog-base/pattern/atom/Badge';
import YoutubeVideo from '@mediashop/base/pattern/molecule/YoutubeVideo';
import { FormattedMessage } from 'react-intl';
import { SlugProductEntity, productVariantSelected } from '@mediashop/app/store/reducer/product';
import SwiperCore from 'swiper';
import { SKIP_RENDER } from '@mediashop/app/constants/semanticConstants';
import ProductDetailsHeader from './Content/ProductDetailsHeader';
import { ProductDetailsThumbnailsGallery } from './Gallery/Thumbnails';
import { ProductDetailsGallery } from './Gallery';
import useDeviceType from '@mediashop/app/hooks/useDeviceType';
import { ProductDetailsSeo } from './Seo';
import { useProductDetailsAnalytics } from '@mediashop/app/analytics/hooks/useProductDetailsAnalytics';
import { useBadges } from '../../../hooks/useBadges';
import { ProductDetailsContainerProps } from '.';
import { filterProductVariantsWithoutPrice, selectProductDetailImages } from './utils';
import { BreakpointName } from '@mediashop/app/config/breakpoints';
import { useQueryParams } from '@mediashop/app/hooks/useQueryParams';
import { BrxPageModel } from '@mediashop/app/bloomreach/types';
import { AccordionPDPProps } from '../AccordionPDP/AccordionPDP';
import { BrPageContext } from '@bloomreach/react-sdk';
import { AccordionItemPDP } from '../AccordionPDP/types';
import { getProductSlug } from '@mediashop/app/helper/getProductSlug';

const componentName = 'product-details';

type ProductDetailsProps = ProductDetailsContainerProps & {
    product: SlugProductEntity;
};

// eslint-disable-next-line max-lines-per-function,complexity
export default function ProductDetails({
    hideAddToCartBar,
    infotextForPaymentByInstallments,
    product: foundProduct,
    productRelationType,
    shippingCostsInfo,
}: ProductDetailsProps): ReactElement {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const dispatchProductDetails = useProductDetailsAnalytics();

    const deviceType = useDeviceType();
    const [device, setDevice] = useState<BreakpointName>('desktop');

    useEffect(() => setDevice(deviceType), [deviceType]);

    const isDeviceTypeMobile = device === 'mobile';
    const isDesktopUp = device === 'desktop' || device === 'wide';

    const {
        slugProduct: { selectedVariant, isSelectedVariantSelectedByUser: isVariantSelected },
    } = useProduct();

    const product = useMemo(
        () => filterProductVariantsWithoutPrice(foundProduct ?? ({} as SlugProductEntity)),
        [foundProduct]
    );

    const [showSubscriptionProduct, setShowSubscriptionProduct] = useState<boolean>(false);
    useEffect(() => {
        const slug = getProductSlug(location.pathname);
        if (product?.subscriptionProduct?.slug === slug) {
            setShowSubscriptionProduct(true);
        } else {
            setShowSubscriptionProduct(false);
        }
    }, []);

    const onSubscriptionChanged = (isSubscriptionVariant: boolean) => setShowSubscriptionProduct(isSubscriptionVariant);

    const getCurrentProduct = () => (showSubscriptionProduct ? product?.subscriptionProduct : product);

    const isOnlyOneVariantActive = product?.variants.length === 1;

    const [thumbnailSlider, setThumbnailSlider] = useState<SwiperCore>();
    const [mainSlider, setMainSlider] = useState<SwiperCore>();
    const [showModal, setShowModal] = useState(false);
    const [imageIndex, setImageIndex] = useState(0);
    const [showMobileImageGallery, setShowMobileImageGallery] = useState<boolean>(false);

    const videoHidden = useMemo(() => imageIndex > 0, [imageIndex]);

    const sortedPictures = useMemo(
        () => selectProductDetailImages(isVariantSelected, selectedVariant, isOnlyOneVariantActive),
        [isVariantSelected, selectedVariant, isOnlyOneVariantActive]
    );

    const { topBadges, bottomBadges, detailBadges, hasSaleBadge } = useBadges({
        product: getCurrentProduct(),
        variant: selectedVariant,
        isPDP: true,
    });

    const { variant: slugVariant } = useQueryParams();
    useEffect(() => {
        if (!isVariantSelected && slugVariant) {
            dispatch(productVariantSelected({ sku: slugVariant }));
        }
    }, []);

    useEffect(() => {
        if (mainSlider && !mainSlider.destroyed) {
            mainSlider.slideTo(0);
        }
    }, [isVariantSelected, selectedVariant, mainSlider]);

    /**
     * Dispatch product analytics.
     * We only dispatch it once for each product OR when the selected variant changes.
     */
    useEffect(
        () => {
            if (product) {
                const variant = isVariantSelected || product.variants.length === 1 ? selectedVariant : undefined;
                dispatchProductDetails({ product, variant });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatchProductDetails, product]
    );

    const openCloseModal = () => {
        setShowModal(!showModal);
    };

    const beforeChange = (sliderIndex: number) => {
        thumbnailSlider?.update();
        setImageIndex(sliderIndex);
    };

    const onVariantChange = (variant: DetailedVariant) => {
        if (product && variant) {
            dispatch(productVariantSelected({ sku: variant.sku }));
            dispatchProductDetails({ product, variant });
        }
    };

    const hasProductVideo =
        selectedVariant?.attributes?.useProductVideo !== '0' && selectedVariant?.attributes?.youtubeVideoLink;

    const brCtx: any = useContext(BrPageContext);
    const anchorComponents: AccordionItemPDP[] = Object.values(brCtx.model.page as BrxPageModel)
        .filter((component) => {
            const data: AccordionPDPProps = component.data;
            return data?.contentType === 'mediashop:AccordionPDP' && !data.hideVideos;
        })
        .flatMap((anchorItem) =>
            anchorItem.data.items.filter(
                (accordionItem) => accordionItem.anchor?.text && accordionItem.accordionEntryType === 'videos'
            )
        );

    const goToAnchor = (id?: string) => {
        if (!id) {
            return;
        }

        const anchor = document.getElementById(id);
        if (anchor) {
            const anchorTopPosition = anchor.offsetTop;
            window.scrollTo({ top: anchorTopPosition, behavior: 'smooth' });

            const clickableAnchor = anchor.getElementsByClassName('accordion-pdp-item__heading');
            const anchorAccordionItem = anchor.getElementsByClassName('accordion-pdp-item__item');
            if (clickableAnchor.length && !anchorAccordionItem.length) {
                (clickableAnchor[0] as HTMLElement).click();
            }
        }
    };

    const showVideo = useCallback(() => {
        if (!anchorComponents[0]) {
            setShowModal(true);
        } else {
            goToAnchor(anchorComponents[0].anchor?.id);
        }
    }, [anchorComponents]);

    useEffect(() => {
        if (location.hash.includes('#showvideo') && hasProductVideo) {
            showVideo();
        }
    }, [location.hash, hasProductVideo, showVideo]);

    const currentProduct = getCurrentProduct();

    return (
        <BackgroundColorWrapper backgroundColor="default">
            <ContentWrapper>
                {currentProduct && selectedVariant ? (
                    <div className={`${componentName}--wrapper`}>
                        {/* SEO Tags */}
                        <ProductDetailsSeo product={currentProduct} mainImage={sortedPictures[0]} />
                        {isDeviceTypeMobile ? (
                            <ProductDetailsHeader
                                className={`${componentName}__mobile-header`}
                                title={
                                    isVariantSelected && !showSubscriptionProduct
                                        ? selectedVariant.attributes.name
                                        : currentProduct.name
                                }
                                claim={selectedVariant.attributes.claim}
                                stars={currentProduct.variants[0].attributes.ratingValueLocalized ?? 0}
                                reviewCount={currentProduct.variants[0].attributes.ratingCountLocalized ?? 0}
                            />
                        ) : (
                            SKIP_RENDER
                        )}

                        {/* Thumbnail Gallery (not displayed on mobile) */}
                        <ProductDetailsThumbnailsGallery
                            product={currentProduct}
                            hasProductVideo={hasProductVideo}
                            pictures={sortedPictures}
                            setThumbSlider={setThumbnailSlider}
                            showProductVideo={showVideo}
                        />

                        <div
                            className={classNames(`${componentName}--slider-wrapper`, {
                                [`${componentName}--slider-wrapper--has-usp`]: sortedPictures[imageIndex]?.usp,
                            })}
                        >
                            <div className={`${componentName}--slider-container`}>
                                <div>
                                    <ProductDetailsGallery
                                        variantName={selectedVariant.attributes.name}
                                        onBeforeIndexChange={beforeChange}
                                        setMainSlider={setMainSlider}
                                        pictures={sortedPictures}
                                        thumbnailSlider={thumbnailSlider}
                                        openMobileImageGallery={() => setShowMobileImageGallery(true)}
                                    />
                                    <div className={`${componentName}__badge-wrapper`}>
                                        <div
                                            className={classNames(
                                                `${componentName}__badge-container`,
                                                `${componentName}__badge-container--top`
                                            )}
                                        >
                                            {topBadges.map(({ badgeProps }) => (
                                                <Badge {...badgeProps} size="large" key={badgeProps.text} />
                                            ))}
                                        </div>

                                        {imageIndex === 0 ? (
                                            <div
                                                className={classNames(
                                                    `${componentName}__badge-container`,
                                                    `${componentName}__badge-container--bottom`
                                                )}
                                            >
                                                {bottomBadges.map((badge) => {
                                                    if (badge.type === 'GRATISZUBEHOER' && isDeviceTypeMobile) {
                                                        return (
                                                            <Badge
                                                                text={badge.badgeProps.text}
                                                                size="large"
                                                                style={badge.badgeProps.style}
                                                                cornerPosition={badge.badgeProps.cornerPosition}
                                                                key={badge.badgeProps.text}
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <Badge
                                                            {...badge.badgeProps}
                                                            size={isDesktopUp ? 'xlarge' : 'large'}
                                                            key={badge.badgeProps.text}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            SKIP_RENDER
                                        )}
                                    </div>
                                </div>
                            </div>

                            {hasProductVideo && (
                                <div
                                    className={classNames(
                                        `${componentName}--video-wrapper-mobile`,
                                        videoHidden && `${componentName}--video-wrapper-mobile-hidden`,
                                        { [`${componentName}--video-wrapper-mobile--badges`]: topBadges.length === 2 }
                                    )}
                                    onClick={showVideo}
                                >
                                    <Icon name="PlayButton" className={`${componentName}__playbutton`} />
                                    <span className={`${componentName}__videotext`}>
                                        <FormattedMessage id="product.videoThumbnailText" />
                                    </span>
                                </div>
                            )}

                            {sortedPictures && sortedPictures[imageIndex] && (
                                <div
                                    className={classNames(`${componentName}--blue-box`, `background--secondary`, {
                                        [`${componentName}--blue-box-has-usp`]: sortedPictures[imageIndex]?.usp,
                                    })}
                                >
                                    {sortedPictures[imageIndex]?.usp ?? SKIP_RENDER}
                                </div>
                            )}
                        </div>
                        <div className={`${componentName}--content-wrapper`}>
                            <div id={`${componentName}--image-portal`} />
                            {!isDeviceTypeMobile ? (
                                <ProductDetailsHeader
                                    className={`${componentName}__desktop-header`}
                                    title={
                                        isVariantSelected && !showSubscriptionProduct
                                            ? selectedVariant.attributes.name
                                            : currentProduct.name
                                    }
                                    claim={selectedVariant.attributes.claim}
                                    stars={currentProduct.variants[0].attributes.ratingValueLocalized ?? 0}
                                    reviewCount={currentProduct.variants[0].attributes.ratingCountLocalized ?? 0}
                                />
                            ) : (
                                SKIP_RENDER
                            )}
                            <ProductDetailsContent
                                product={{ ...foundProduct, product }}
                                activeVariant={selectedVariant}
                                setVariant={onVariantChange}
                                detailBadges={detailBadges}
                                hasSaleBadge={hasSaleBadge}
                                shippingCostsInfo={shippingCostsInfo}
                                productRelationType={productRelationType}
                                infoTextForPaymentByInstallments={infotextForPaymentByInstallments}
                                showSubscriptionProduct={showSubscriptionProduct}
                                onSubscriptionChanged={onSubscriptionChanged}
                                isVariantSelected={isVariantSelected}
                                variantImage={sortedPictures[0]}
                                hideAddToCartBar={hideAddToCartBar}
                            />
                        </div>

                        {hasProductVideo ? (
                            <Modal
                                widthClassName={`${componentName}__videomodal`}
                                isOpen={showModal}
                                onRequestClose={openCloseModal}
                                overlayClassName="modal__overlay-bg"
                            >
                                <YoutubeVideo videoUrl={selectedVariant?.attributes?.youtubeVideoLink} />
                            </Modal>
                        ) : (
                            SKIP_RENDER
                        )}
                    </div>
                ) : (
                    SKIP_RENDER
                )}
                {showMobileImageGallery ? (
                    <MobileImageGallery
                        initIndex={imageIndex}
                        images={sortedPictures}
                        closeGallery={() => setShowMobileImageGallery(false)}
                    />
                ) : (
                    SKIP_RENDER
                )}
            </ContentWrapper>
        </BackgroundColorWrapper>
    );
}
